import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, UserPlus } from "lucide-react";
import { ApiError, type LoyaltyLookupDto, type LoyaltyProgramDto } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";
import { StampRules } from "./StampRules";

const MODULE_KEY = "loyalti";
const inputCls = "h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";

function errMsg(err: unknown, fallback: string) {
  if (err instanceof ApiError) {
    const m = err.response.message;
    return Array.isArray(m) ? m.join(" ") : m;
  }
  return fallback;
}

export function LojaltiPage() {
  const qc = useQueryClient();
  const program = useQuery({ queryKey: ["loyalty-program"], queryFn: () => api.getLoyaltyProgram(), retry: false });
  const stats = useQuery({ queryKey: ["loyalty-stats"], queryFn: () => api.getLoyaltyStats(), enabled: program.isSuccess });
  const products = useQuery({ queryKey: ["loyalty-products"], queryFn: () => api.listProducts(), enabled: program.isSuccess });

  const [form, setForm] = useState<LoyaltyProgramDto | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (program.data) setForm(program.data);
  }, [program.data]);

  const save = useMutation({
    mutationFn: (f: LoyaltyProgramDto) => api.saveLoyaltyProgram(f),
    onSuccess: () => {
      setMsg({ ok: true, text: "Sačuvano." });
      qc.invalidateQueries({ queryKey: ["loyalty-program"] });
    },
    onError: (e) => setMsg({ ok: false, text: errMsg(e, "Čuvanje nije uspelo.") }),
  });

  if (program.isError) {
    if (program.error instanceof ApiError && program.error.response.errorCode === "MODULE_NOT_AVAILABLE") {
      return <ModuleGateNotice error={program.error} moduleKey={MODULE_KEY} />;
    }
    return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(program.error, "Učitavanje nije uspelo.")}</p>;
  }
  if (!form) return <Skeleton className="h-40 w-full" />;

  const set = <K extends keyof LoyaltyProgramDto>(k: K, v: LoyaltyProgramDto[K]) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Lojalti program</h1>
        <p className="text-sm text-slate-500">
          Kupci skupljaju pečate (npr. 10 kafa, 11. besplatna) ili bodove. Kasir ili konobar unese ili skenira kupčev lojalti ID uz porudžbinu,
          a pečati i bodovi se upišu sami čim se račun izda.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ["Članova", stats.data?.members],
          ["Podeljeno pečata", stats.data?.total_stamps],
          ["Besplatnih čeka", stats.data?.rewards_waiting],
          ["Bodova kod kupaca", stats.data?.points_outstanding],
        ].map(([label, v]) => (
          <div key={String(label)} className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
            <div className="text-xs text-slate-500">{label}</div>
            <div className="text-xl font-semibold text-slate-900">{v ?? "—"}</div>
          </div>
        ))}
      </div>

      <div className="space-y-4 rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
          <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} /> Program je uključen
        </label>

        <div className="border-t border-border pt-3">
          <StampRules rules={form.rules} products={products.data ?? []} onChanged={() => qc.invalidateQueries({ queryKey: ["loyalty-program"] })} />
        </div>

        <div className="space-y-2 border-t border-border pt-3">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
            <input type="checkbox" checked={form.points_enabled} onChange={(e) => set("points_enabled", e.target.checked)} /> Bodovi po iznosu računa
          </label>
          {form.points_enabled && (
            <div className="grid max-w-xl grid-cols-2 gap-3">
              <label className="text-xs text-slate-500">
                Bodova na svakih 100 din
                <input className={inputCls} type="number" min={0} step="0.1" value={form.points_per_100} onChange={(e) => set("points_per_100", Number(e.target.value) || 0)} />
              </label>
              <label className="text-xs text-slate-500">
                Vrednost jednog boda (din)
                <input className={inputCls} type="number" min={0} step="0.1" value={form.point_value} onChange={(e) => set("point_value", Number(e.target.value) || 0)} />
              </label>
            </div>
          )}
        </div>

        <div className="border-t border-border pt-3">
          <label className="block max-w-xs text-xs text-slate-500">
            Rok za upis pečata/bodova posle izdavanja računa (sati)
            <input
              className={inputCls}
              type="number"
              min={1}
              max={720}
              value={form.receipt_window_hours}
              onChange={(e) => set("receipt_window_hours", Math.max(1, Number(e.target.value) || 48))}
            />
          </label>
        </div>

        <div className="flex items-center gap-3">
          <Button disabled={save.isPending} onClick={() => save.mutate(form)}>
            Sačuvaj
          </Button>
          {msg && <span className={`text-sm ${msg.ok ? "text-emerald-600" : "text-red-600"}`}>{msg.text}</span>}
        </div>
      </div>

      <MembersBox />
    </div>
  );
}

function MembersBox() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [created, setCreated] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [found, setFound] = useState<LoyaltyLookupDto | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const reg = useMutation({
    mutationFn: () => api.registerLoyaltyCustomer({ name: name || undefined, phone: phone || undefined }),
    onSuccess: (r) => {
      setErr(null);
      setCreated(`${r.existing ? "Već je član. " : "Novi član. "}Lojalti ID: ${r.loyalty_id}`);
    },
    onError: (e) => setErr(errMsg(e, "Upis nije uspeo.")),
  });
  const look = useMutation({
    mutationFn: () => api.lookupLoyaltyCustomer(code),
    onSuccess: (r) => {
      setErr(null);
      setFound(r);
    },
    onError: (e) => {
      setFound(null);
      setErr(errMsg(e, "Pretraga nije uspela."));
    },
  });

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="space-y-2 rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <UserPlus className="h-4 w-4" aria-hidden /> Upiši novog člana
        </h2>
        <input className={inputCls} placeholder="Ime (neobavezno)" value={name} onChange={(e) => setName(e.target.value)} />
        <input className={inputCls} placeholder="Telefon" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <Button disabled={reg.isPending || (!name && !phone)} onClick={() => reg.mutate()}>
          Upiši
        </Button>
        {created && <p className="text-sm text-emerald-700">{created}</p>}
      </div>
      <div className="space-y-2 rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Search className="h-4 w-4" aria-hidden /> Proveri stanje kupca
        </h2>
        <input className={inputCls} placeholder="Lojalti ID ili telefon" value={code} onChange={(e) => setCode(e.target.value)} />
        <Button disabled={look.isPending || !code} onClick={() => look.mutate()}>
          Proveri
        </Button>
        {found && (
          <div className="text-sm text-slate-700">
            <div className="font-medium">
              {found.name || "Bez imena"} · {found.loyalty_id}
            </div>
            {found.card.stamp_cards.map((c) => (
              <div key={c.rule_id}>
                {c.name}: {c.stamps} / {c.required} · Besplatnih čeka: {c.rewards}
              </div>
            ))}
            <div>
              Bodovi: {found.card.points} ({found.card.points_value_total.toLocaleString("sr-RS")} din)
            </div>
          </div>
        )}
      </div>
      {err && <p className="md:col-span-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
    </div>
  );
}
